/**
 * FilesPage — production-grade file browser.
 *
 * Maps to docs/chatwork_killer/06_task_file_org.md §2 (Files):
 *   - Folder tree (left rail) + grid (right)
 *   - Drag-and-drop upload zone (HTML5 DnD; presigned R2 wired in #5993)
 *   - Preview modal (image / PDF / video)
 *   - Share-link dialog with server issued TTL and password protection
 *   - Filters: extension, size, modified
 *   - Storage usage bar against the workspace plan limit
 *
 * File IO uses authenticated API requests and signed object storage URLs.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  File as FileIcon,
  FileText,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  Link2,
  Search,
  Trash2,
  Upload,
  Video as VideoIcon,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  FolderEditor,
  FileOrganizationDialog,
  FileTrashView,
} from "@/components/FileLibraryControls";
import {
  useFileLibrary,
  organizeFile,
  refreshFileLibrary,
} from "@/lib/file-library";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { TopBar } from "@/components/TopBar";
import { useQuery } from "@tanstack/react-query";
import { useMembers, useWorkspace } from "@/lib/workspace";
import { useSession } from "@/lib/auth";
import { useRooms } from "@/lib/rooms";
import {
  useFiles,
  uploadFile,
  deleteFile,
  downloadFile,
  fileDownloadUrl,
  createFileShare,
  listFileShares,
  revokeFileShare,
} from "@/lib/files";
import { formatBytes, formatRelative } from "@/lib/format";
import { cn } from "@/lib/cn";
import type { FileEntry } from "@/lib/types";

interface FolderNode {
  id: string;
  name: string;
  count?: number;
  children?: FolderNode[];
}

type SizeBucket = "all" | "lt1mb" | "1mb_10mb" | "gt10mb";
type ExtBucket = "all" | "image" | "document" | "video" | "other";
type ModifiedBucket = "all" | "today" | "week" | "month";

interface Filters {
  query: string;
  ext: ExtBucket;
  size: SizeBucket;
  modified: ModifiedBucket;
}

const INITIAL_FILTERS: Filters = {
  query: "",
  ext: "all",
  size: "all",
  modified: "all",
};

export function FilesPage(): JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const workspace = useWorkspace();
  const { rooms } = useRooms();
  const { files, loading, error: loadError, refresh } = useFiles();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const perform = async (operation: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const [activeFolder, setActiveFolder] = useState("all");
  const library = useFileLibrary();
  const [folderEditor, setFolderEditor] = useState<"new" | "edit" | null>(null);
  const [organizing, setOrganizing] = useState<FileEntry | null>(null);
  const libraryFolders = library.data?.folders ?? [];
  const selectedFolder = libraryFolders.find(
    (folder) => `folder:${folder.id}` === activeFolder,
  );
  const canManageFolder =
    selectedFolder &&
    (selectedFolder.created_by === session?.user.id ||
      ["owner", "admin"].includes(workspace.data?.role ?? ""));

  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [preview, setPreview] = useState<FileEntry | null>(null);
  const [share, setShare] = useState<FileEntry | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const customFolderNodes = new Map(
    libraryFolders.map((folder) => [
      folder.id,
      {
        id: `folder:${folder.id}`,
        name: folder.name,
        count: files.filter((file) => file.folderId === folder.id).length,
        children: [] as FolderNode[],
      },
    ]),
  );
  const customRoots: FolderNode[] = [];
  for (const folder of libraryFolders) {
    const node = customFolderNodes.get(folder.id)!;
    const parent = folder.parent_id
      ? customFolderNodes.get(folder.parent_id)
      : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else customRoots.push(node);
  }
  const folders: FolderNode[] = [
    { id: "all", name: "すべてのファイル", count: files.length },
    {
      id: "mine",
      name: "自分のファイル",
      count: files.filter((f) => f.uploaderId === session?.user.id).length,
    },
    {
      id: "starred",
      name: "お気に入り",
      count: files.filter((f) => library.data?.starred_file_ids.includes(f.id))
        .length,
    },
    { id: "trash", name: "ごみ箱" },
    ...customRoots,
    ...rooms.map((room) => ({
      id: room.id,
      name: room.name,
      count: files.filter((f) => f.roomId === room.id).length,
    })),
  ];
  // Filtered file list
  const visible = useMemo(
    () =>
      applyFilters(
        files.filter(
          (file) =>
            activeFolder === "all" ||
            (activeFolder === "mine"
              ? file.uploaderId === session?.user.id
              : activeFolder === "starred"
                ? library.data?.starred_file_ids.includes(file.id)
                : activeFolder.startsWith("folder:")
                  ? file.folderId === activeFolder.slice(7)
                  : file.roomId === activeFolder),
        ),
        filters,
      ),
    [files, filters, activeFolder, session?.user.id, library.data],
  );

  // Storage usage
  const used = useMemo(
    () => files.reduce((sum, f) => sum + f.size, 0),
    [files],
  );

  /* ---- Drop / picker ---------------------------------------------------- */

  const handleFiles = useCallback(
    (dropped: FileList | File[]) => {
      const arr = Array.from(dropped);
      if (!arr.length) return;
      if (activeFolder === "trash") return;
      void perform(async () => {
        const roomId =
          selectedFolder?.room_id ??
          (rooms.some((room) => room.id === activeFolder)
            ? activeFolder
            : undefined);
        for (const file of arr) {
          const uploaded = await uploadFile(file, roomId || undefined);
          if (selectedFolder)
            await organizeFile(uploaded.id, {
              folder_id: selectedFolder.id,
            });
        }
        await refresh();
      });
    },
    [refresh, activeFolder, selectedFolder, rooms],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );
  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  }, []);
  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.currentTarget === e.target) setDragging(false);
  }, []);

  const onPickerChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) handleFiles(e.target.files);
      e.target.value = "";
    },
    [handleFiles],
  );

  const removeFile = (id: string) => {
    if (window.confirm("このファイルをごみ箱へ移動しますか？"))
      void perform(async () => {
        await deleteFile(id);
        await refreshFileLibrary();
      });
  };

  /* ---- Render ----------------------------------------------------------- */

  return (
    <>
      <TopBar
        title={t("files.title")}
        subtitle={`${files.length} ファイル · ${formatBytes(used)} 使用中`}
        trailing={
          <Button
            size="sm"
            leadingIcon={<Upload size={14} aria-hidden />}
            disabled={busy || activeFolder === "trash"}
            onClick={() => fileInputRef.current?.click()}
          >
            {t("files.upload")}
          </Button>
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={onPickerChange}
      />

      <div className="flex flex-1 min-h-0 bg-surface-base">
        {/* Left rail: folder tree + storage */}
        <aside
          aria-label="フォルダツリー"
          className="hidden w-64 flex-col border-r border-border-subtle bg-canvas md:flex"
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFolderEditor("new")}
            >
              フォルダーを作成
            </Button>
            {canManageFolder && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setFolderEditor("edit")}
              >
                選択フォルダーの設定
              </Button>
            )}
            <FolderTree
              tree={folders}
              fileCount={files.length}
              activeId={activeFolder}
              onSelect={setActiveFolder}
            />
          </div>
          <div className="mt-auto border-t border-border-subtle p-4">
            <p className="text-xs font-semibold text-fg-secondary">
              ストレージ使用量
            </p>
            <p className="mt-2 text-xs text-fg-muted">
              閲覧できるファイル: {formatBytes(used)}
            </p>

          </div>
        </aside>

        {/* Right pane */}
        <div
          className="relative flex flex-1 flex-col overflow-hidden"
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {(error || loadError || library.error) && (
            <p role="alert" className="p-3 text-danger">
              {error || loadError?.message || library.error?.message}
              <button
                className="ml-3 underline"
                onClick={() => {
                  void refresh();
                  void library.refetch();
                }}
              >
                再読み込み
              </button>
            </p>
          )}
          {(busy || loading) && (
            <p role="status" className="p-3">
              {busy
                ? "ファイルを処理しています…"
                : "ファイルを読み込んでいます…"}
            </p>
          )}
          <div className="flex items-center gap-2 p-3 md:hidden">
            <select
              aria-label="ファイルの表示場所"
              value={activeFolder}
              onChange={(e) => setActiveFolder(e.target.value)}
              className="min-w-0 flex-1 rounded border border-border-default bg-canvas p-2"
            >
              {flattenFolders(folders).map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFolderEditor("new")}
            >
              新規
            </Button>
            {canManageFolder && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setFolderEditor("edit")}
              >
                設定
              </Button>
            )}
          </div>
          {/* Filter bar */}
          {activeFolder !== "trash" && (
            <FilterBar filters={filters} onChange={setFilters} />
          )}

          {/* Grid */}
          <div className="flex-1 overflow-auto p-4">
            {activeFolder === "trash" ? (
              <FileTrashView />
            ) : visible.length === 0 ? (
              <EmptyState onUpload={() => fileInputRef.current?.click()} />
            ) : (
              <ul
                className="grid gap-3"
                style={{
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                }}
              >
                {visible.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    onPreview={() => setPreview(file)}
                    onShare={() => setShare(file)}
                    onDelete={
                      file.uploaderId === session?.user.id ||
                      ["owner", "admin"].includes(workspace.data?.role ?? "")
                        ? () => removeFile(file.id)
                        : undefined
                    }
                    onOrganize={() => setOrganizing(file)}
                    onDownload={() => void perform(() => downloadFile(file))}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Drop zone overlay */}
          {dragging && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-brand-50/85 ring-2 ring-inset ring-brand-500"
            >
              <div className="flex flex-col items-center gap-2 text-brand-700">
                <Upload size={32} aria-hidden />
                <p className="text-md font-semibold">{t("files.drop")}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {folderEditor && (
        <FolderEditor
          folder={folderEditor === "edit" ? selectedFolder : undefined}
          parentId={folderEditor === "new" ? selectedFolder?.id : undefined}
          roomId={
            rooms.some((room) => room.id === activeFolder)
              ? activeFolder
              : undefined
          }
          onClose={() => setFolderEditor(null)}
          onSaved={(id) => setActiveFolder(id ? `folder:${id}` : "all")}
        />
      )}
      {organizing && (
        <FileOrganizationDialog
          key={organizing.id}
          file={organizing}
          folders={libraryFolders}
          starred={
            library.data?.starred_file_ids.includes(organizing.id) ?? false
          }
          onClose={() => setOrganizing(null)}
        />
      )}
      {/* Preview modal */}
      <PreviewModal file={preview} onClose={() => setPreview(null)} />

      {/* Share-link modal */}
      <ShareLinkModal file={share} onClose={() => setShare(null)} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Folder tree                                                                */
/* -------------------------------------------------------------------------- */

interface FolderTreeProps {
  tree: FolderNode[];
  fileCount: number;
  activeId: string;
  onSelect: (id: string) => void;
}

function FolderTree({
  tree,
  fileCount,
  activeId,
  onSelect,
}: FolderTreeProps): JSX.Element {
  return (
    <ul className="space-y-0.5">
      {tree.map((node) => (
        <FolderTreeNode
          key={node.id}
          node={node}
          rootCount={node.id === "all" ? fileCount : node.count}
          activeId={activeId}
          onSelect={onSelect}
          depth={0}
        />
      ))}
    </ul>
  );
}

interface FolderTreeNodeProps {
  node: FolderNode;
  rootCount?: number;
  activeId: string;
  onSelect: (id: string) => void;
  depth: number;
}

function flattenFolders(
  nodes: FolderNode[],
  depth = 0,
): Array<{ id: string; name: string }> {
  return nodes.flatMap((node) => [
    { id: node.id, name: `${"　".repeat(depth)}${node.name}` },
    ...flattenFolders(node.children ?? [], depth + 1),
  ]);
}
function FolderTreeNode({
  node,
  rootCount,
  activeId,
  onSelect,
  depth,
}: FolderTreeNodeProps): JSX.Element {
  const [open, setOpen] = useState(true);
  const hasChildren = !!node.children?.length;
  const active = activeId === node.id;
  return (
    <li>
      <div
        className={cn(
          "flex items-center rounded-md text-sm",
          active
            ? "bg-brand-50 text-brand-700"
            : "text-fg-secondary hover:bg-surface-raised",
        )}
        style={{ paddingLeft: 8 + depth * 12 }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={`${node.name}を${open ? "閉じる" : "開く"}`}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex h-8 w-6 shrink-0 items-center justify-center rounded focus-visible:shadow-focus"
          >
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <button
          type="button"
          aria-current={active ? "page" : undefined}
          onClick={() => onSelect(node.id)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-2 text-left focus-visible:shadow-focus"
        >
          {hasChildren && open ? (
            <FolderOpen size={14} className="shrink-0" />
          ) : (
            <Folder size={14} className="shrink-0" />
          )}
          <span className="grow truncate">{node.name}</span>
          {rootCount !== undefined && (
            <span className="pr-2 text-[11px] text-fg-muted">{rootCount}</span>
          )}
        </button>
      </div>
      {hasChildren && open && (
        <ul>
          {node.children!.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              rootCount={child.count}
              activeId={activeId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Filter bar                                                                 */
/* -------------------------------------------------------------------------- */

interface FilterBarProps {
  filters: Filters;
  onChange: (next: Filters) => void;
}

function FilterBar({ filters, onChange }: FilterBarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle bg-canvas px-4 py-3">
      <label className="relative grow min-w-[200px] max-w-md">
        <Search
          size={14}
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted"
        />
        <Input
          type="search"
          aria-label="ファイルを検索"
          placeholder="ファイル名で検索..."
          className="!pl-8"
          value={filters.query}
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
        />
      </label>

      <SelectFilter
        label="種別"
        value={filters.ext}
        options={[
          { value: "all", label: "すべて" },
          { value: "image", label: "画像" },
          { value: "document", label: "書類 (PDF・Office)" },
          { value: "video", label: "動画" },
          { value: "other", label: "その他" },
        ]}
        onChange={(v) => onChange({ ...filters, ext: v as ExtBucket })}
      />
      <SelectFilter
        label="サイズ"
        value={filters.size}
        options={[
          { value: "all", label: "すべて" },
          { value: "lt1mb", label: "1MB 未満" },
          { value: "1mb_10mb", label: "1〜10MB" },
          { value: "gt10mb", label: "10MB 超" },
        ]}
        onChange={(v) => onChange({ ...filters, size: v as SizeBucket })}
      />
      <SelectFilter
        label="更新日"
        value={filters.modified}
        options={[
          { value: "all", label: "すべて" },
          { value: "today", label: "今日" },
          { value: "week", label: "1週間以内" },
          { value: "month", label: "1ヶ月以内" },
        ]}
        onChange={(v) =>
          onChange({ ...filters, modified: v as ModifiedBucket })
        }
      />

      {!isPristine(filters) && (
        <button
          type="button"
          onClick={() => onChange(INITIAL_FILTERS)}
          className="rounded-md border border-border-default bg-canvas px-3 py-1.5 text-xs text-fg-secondary hover:bg-surface-raised"
        >
          リセット
        </button>
      )}
    </div>
  );
}

interface SelectFilterProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
}: SelectFilterProps): JSX.Element {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs text-fg-secondary">
      <span className="text-fg-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* File card                                                                  */
/* -------------------------------------------------------------------------- */

interface FileCardProps {
  file: FileEntry;
  onPreview: () => void;
  onShare: () => void;
  onDelete?: () => void;
  onOrganize: () => void;
  onDownload: () => void;
}

function FileCard({
  file,
  onPreview,
  onShare,
  onDelete,
  onOrganize,
  onDownload,
}: FileCardProps): JSX.Element {
  const { t } = useTranslation();
  const Icon = iconForMime(file.mime);
  const { members } = useMembers();
  const owner = members.find((member) => member.id === file.uploaderId);
  return (
    <li
      data-testid="file-card"
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg bg-canvas ring-1 ring-border-subtle transition",
        "hover:-translate-y-0.5 hover:shadow-md focus-within:shadow-md",
      )}
    >
      <button
        type="button"
        onClick={onPreview}
        className="flex aspect-video w-full items-center justify-center bg-surface-base text-fg-muted transition group-hover:bg-surface-raised"
        aria-label={`${file.name} をプレビュー`}
      >
        <Icon size={36} aria-hidden />
      </button>

      <div className="flex flex-col gap-1 p-3">
        <p
          className="truncate text-sm font-medium text-fg-primary"
          title={file.name}
        >
          {file.name}
        </p>
        <div className="flex items-center justify-between text-[11px] text-fg-muted">
          <span>{formatBytes(file.size)}</span>
          <span>{formatRelative(file.uploadedAt)}</span>
        </div>
        {owner && (
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-fg-secondary">
            <Avatar name={owner.displayName} size={20} />
            <span className="truncate">{owner.displayName}</span>
          </div>
        )}

        <div className="mt-2 flex items-center gap-1">
          <Button
            size="sm"
            variant="secondary"
            className="flex-1 whitespace-nowrap px-2"
            leadingIcon={<Download size={12} aria-hidden />}
            onClick={onDownload}
          >
            {t("files.download")}
          </Button>
          <button
            type="button"
            onClick={onShare}
            aria-label={`${file.name} を共有`}
            title="共有リンク"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-default bg-canvas text-fg-secondary hover:bg-surface-raised"
          >
            <Link2 size={14} aria-hidden />
          </button>
          <button
            type="button"
            onClick={onOrganize}
            aria-label={`${file.name} を整理`}
            title="フォルダーとお気に入り"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-default bg-canvas text-fg-secondary hover:bg-surface-raised"
          >
            <Folder size={14} aria-hidden />
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label={`${file.name} を削除`}
              title="削除"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-default bg-canvas text-fg-secondary hover:bg-danger-bg hover:text-danger"
            >
              <Trash2 size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty state                                                                */
/* -------------------------------------------------------------------------- */

function EmptyState({ onUpload }: { onUpload: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border-default bg-canvas py-16 text-center">
      <Upload size={28} aria-hidden className="text-fg-subtle" />
      <p className="text-sm font-medium text-fg-primary">{t("files.empty")}</p>
      <p className="text-xs text-fg-muted">
        ファイルをこのエリアにドラッグするか、ボタンから選択してください。
      </p>
      <Button
        size="sm"
        leadingIcon={<Upload size={14} aria-hidden />}
        onClick={onUpload}
      >
        {t("files.upload")}
      </Button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Preview modal                                                              */
/* -------------------------------------------------------------------------- */

function PreviewModal({
  file,
  onClose,
}: {
  file: FileEntry | null;
  onClose: () => void;
}): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setUrl(null);
    setError(null);
    let cancelled = false;
    if (file)
      void fileDownloadUrl(file.id)
        .then((value) => {
          if (!cancelled) setUrl(value);
        })
        .catch((e) => {
          if (!cancelled) setError(String(e));
        });
    return () => {
      cancelled = true;
    };
  }, [file?.id]);
  const { members } = useMembers();
  if (!file) return null;
  const isImage = file.mime.startsWith("image/");
  const isPdf = file.mime === "application/pdf";
  const isVideo = file.mime.startsWith("video/");
  const owner = members.find((member) => member.id === file.uploaderId);

  return (
    <Modal open onClose={onClose} size="lg" title={file.name}>
      <div className="flex flex-col gap-4">
        <div className="flex aspect-video items-center justify-center rounded-md bg-surface-sunken text-fg-muted">
          {error ? (
            <p role="alert">{error}</p>
          ) : !url ? (
            <p role="status">プレビューを読み込んでいます…</p>
          ) : isImage ? (
            <img
              src={url}
              alt={file.name}
              className="h-full w-full object-contain"
            />
          ) : isPdf ? (
            <iframe
              title={file.name}
              src={url}
              className="h-full min-h-96 w-full"
            />
          ) : isVideo ? (
            <video controls src={url} className="h-full w-full" />
          ) : (
            <p>この形式はダウンロードして開けます。</p>
          )}
        </div>

        <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs">
          <dt className="text-fg-muted">サイズ</dt>
          <dd className="col-span-2 text-fg-primary">
            {formatBytes(file.size)}
          </dd>
          <dt className="text-fg-muted">種別</dt>
          <dd className="col-span-2 text-fg-primary">{file.mime}</dd>
          <dt className="text-fg-muted">更新日</dt>
          <dd className="col-span-2 text-fg-primary">
            {formatRelative(file.uploadedAt)}
          </dd>
          {owner && (
            <>
              <dt className="text-fg-muted">アップロード者</dt>
              <dd className="col-span-2 text-fg-primary">
                {owner.displayName}
              </dd>
            </>
          )}
        </dl>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            leadingIcon={<Download size={14} aria-hidden />}
            onClick={() =>
              void downloadFile(file).catch((e) => setError(String(e)))
            }
          >
            ダウンロード
          </Button>
          <Button onClick={onClose}>閉じる</Button>
        </div>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Share-link modal                                                           */
/* -------------------------------------------------------------------------- */

function ShareLinkModal({
  file,
  onClose,
}: {
  file: FileEntry | null;
  onClose: () => void;
}): JSX.Element | null {
  const [ttl, setTtl] = useState("7days");
  const [withPassword, setWithPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const [link, setLink] = useState("");
  const [shareId, setShareId] = useState("");
  const activeFileId = useRef(file?.id);
  activeFileId.current = file?.id;
  const [revoked, setRevoked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [charge, setCharge] = useState<number | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const activeShares = useQuery({
    queryKey: ["file-shares", file?.id],
    enabled: !!file,
    queryFn: () => listFileShares(file!.id),
  });
  async function revokeListed(id: string) {
    if (!window.confirm("この共有リンクを失効させますか？")) return;
    setRevokingId(id);
    setListError(null);
    try {
      await revokeFileShare(id);
      if (id === shareId) {
        setRevoked(true);
        setLink("");
      }
      await activeShares.refetch();
    } catch (e) {
      setListError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevokingId(null);
    }
  }
  async function issue() {
    if (!file) return;
    setBusy(true);
    setError(null);
    const target = file.id;
    try {
      const result = await createFileShare(
        file.id,
        ttl === "1day" ? 24 : 168,
        withPassword ? password : undefined,
      );
      if (activeFileId.current !== target) return;
      setLink(result.share_url);
      setShareId(result.id);
      setRevoked(false);
      setCharge(result.charge_jpy);
      void activeShares.refetch();
    } catch (e) {
      if (activeFileId.current === target)
        setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (activeFileId.current === target) setBusy(false);
    }
  }

  useEffect(() => {
    if (!file) {
      setTtl("7days");
      setWithPassword(false);
      setPassword("");
      setCopied(false);
      setLink("");
      setError(null);
      setCharge(null);
      setShareId("");
      setRevoked(false);
      setBusy(false);
      setListError(null);
      setRevokingId(null);
    }
  }, [file]);

  if (!file) return null;

  async function copy(): Promise<void> {
    try {
      if (!link || !navigator.clipboard)
        throw new Error("リンクを先に発行してください");
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="共有リンク"
      description="リンクを知っている人がアクセスできます。"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 rounded-md bg-surface-base p-3 ring-1 ring-border-subtle">
          <FileIcon size={16} aria-hidden className="text-fg-muted" />
          <span className="truncate text-sm font-medium text-fg-primary">
            {file.name}
          </span>
          <Badge tone="neutral" className="ml-auto">
            {formatBytes(file.size)}
          </Badge>
        </div>

        <div className="space-y-3">
          <Field
            label="有効期限"
            helperText="期限を過ぎるとリンクは自動で失効します。"
          >
            <select
              value={ttl}
              disabled={!!link}
              onChange={(e) => setTtl(e.target.value)}
              className="w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm focus-visible:shadow-focus"
            >
              <option value="1day">24時間</option>
              <option value="7days">7日間</option>
            </select>
          </Field>

          <div className="flex items-center justify-between rounded-md border border-border-subtle bg-surface-base p-3">
            <div>
              <p className="text-sm font-medium text-fg-primary">
                パスワードで保護
              </p>
              <p className="text-xs text-fg-muted">
                共有先に別経路で伝えてください。
              </p>
            </div>
            <Toggle
              checked={withPassword}
              disabled={!!link}
              onChange={setWithPassword}
              label="パスワード保護"
            />
          </div>
          {withPassword && (
            <Field label="パスワード" helperText="8文字以上を推奨します。">
              <Input
                type="text"
                value={password}
                disabled={!!link}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="例: a8B!secure"
              />
            </Field>
          )}
        </div>

        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        {charge !== null && (
          <p role="status">
            {revoked
              ? "共有リンクを失効しました。"
              : "共有リンクを発行しました。"}
          </p>
        )}
        {shareId && !revoked && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => {
              if (!window.confirm("この共有リンクを失効させますか？")) return;
              setBusy(true);
              void revokeFileShare(shareId)
                .then(() => {
                  setRevoked(true);
                  setLink("");
                  void activeShares.refetch();
                })
                .catch((error) => setError(error.message))
                .finally(() => setBusy(false));
            }}
          >
            共有リンクを失効する
          </Button>
        )}
        <p className="text-xs text-fg-muted">共有リンクは無料で発行できます。</p>
        <Field label="共有リンク">
          <div className="flex items-center gap-2">
            <Input value={link} readOnly className="!font-mono !text-xs" />
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<Copy size={14} aria-hidden />}
              onClick={() => void copy()}
              disabled={!link}
            >
              {copied ? "コピー済み" : "コピー"}
            </Button>
          </div>
        </Field>

        <section aria-label="有効な共有リンク" className="space-y-2 border-t border-border-subtle pt-4">
          <p className="text-sm font-medium text-fg-primary">有効な共有リンク</p>
          {activeShares.isPending ? (
            <p className="text-xs text-fg-muted">読み込み中…</p>
          ) : activeShares.error ? (
            <p role="alert" className="text-xs text-danger">
              有効な共有リンクを読み込めませんでした。
            </p>
          ) : (activeShares.data ?? []).length === 0 ? (
            <p className="text-xs text-fg-muted">有効な共有リンクはありません。</p>
          ) : (
            <ul className="divide-y divide-border-subtle rounded-md ring-1 ring-border-subtle">
              {(activeShares.data ?? []).map((item) => (
                <li key={item.id} className="flex items-start gap-3 p-3 text-xs">
                  <dl className="grid flex-1 grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-fg-secondary">
                    <dt className="text-fg-muted">発行日時</dt>
                    <dd>{formatShareDate(item.created_at)}</dd>
                    <dt className="text-fg-muted">期限</dt>
                    <dd>{formatShareDate(item.expires_at)}</dd>
                    <dt className="text-fg-muted">パスワード</dt>
                    <dd>{item.has_password ? "あり" : "なし"}</dd>
                    <dt className="text-fg-muted">発行者</dt>
                    <dd>{item.created_by_name ?? "不明なユーザー"}</dd>
                  </dl>
                  {item.can_revoke && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={revokingId !== null}
                      onClick={() => void revokeListed(item.id)}
                    >
                      {revokingId === item.id ? "失効しています…" : "失効する"}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {listError && (
            <p role="alert" className="text-xs text-danger">
              {listError}
            </p>
          )}
        </section>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            onClick={() => void issue()}
            disabled={
              busy ||
              !!link ||
              (withPassword && !password.trim())
            }
          >
            {busy
              ? "発行しています…"
              : "発行する"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function formatShareDate(ms: number): string {
  return new Date(ms).toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* -------------------------------------------------------------------------- */
/* Form helpers                                                               */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  helperText,
  error,
  children,
}: {
  label: string;
  helperText?: string;
  error?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
      {children}
      {error ? (
        <span className="text-xs text-danger">{error}</span>
      ) : helperText ? (
        <span className="text-xs text-fg-muted">{helperText}</span>
      ) : null}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-brand-500" : "bg-fg-subtle",
        "focus-visible:shadow-focus",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-canvas shadow transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Filtering                                                                  */
/* -------------------------------------------------------------------------- */

function applyFilters(files: FileEntry[], filters: Filters): FileEntry[] {
  const q = filters.query.trim().toLowerCase();
  return files.filter((f) => {
    if (q && !f.name.toLowerCase().includes(q)) return false;
    if (!matchExt(f, filters.ext)) return false;
    if (!matchSize(f, filters.size)) return false;
    if (!matchModified(f, filters.modified)) return false;
    return true;
  });
}

function matchExt(f: FileEntry, bucket: ExtBucket): boolean {
  if (bucket === "all") return true;
  if (bucket === "image") return f.mime.startsWith("image/");
  if (bucket === "video") return f.mime.startsWith("video/");
  if (bucket === "document")
    return (
      f.mime === "application/pdf" ||
      f.mime.includes("word") ||
      f.mime.includes("excel") ||
      f.mime.includes("powerpoint") ||
      f.mime.includes("officedocument") ||
      f.mime === "text/plain"
    );
  // other
  return (
    !f.mime.startsWith("image/") &&
    !f.mime.startsWith("video/") &&
    f.mime !== "application/pdf" &&
    !f.mime.includes("word") &&
    !f.mime.includes("excel") &&
    !f.mime.includes("powerpoint") &&
    !f.mime.includes("officedocument") &&
    f.mime !== "text/plain"
  );
}

function matchSize(f: FileEntry, bucket: SizeBucket): boolean {
  if (bucket === "all") return true;
  const mb = f.size / (1024 * 1024);
  if (bucket === "lt1mb") return mb < 1;
  if (bucket === "1mb_10mb") return mb >= 1 && mb <= 10;
  return mb > 10;
}

function matchModified(f: FileEntry, bucket: ModifiedBucket): boolean {
  if (bucket === "all") return true;
  const t = new Date(f.uploadedAt).getTime();
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  if (bucket === "today") return now - t < day;
  if (bucket === "week") return now - t < 7 * day;
  return now - t < 30 * day;
}

function isPristine(f: Filters): boolean {
  return (
    f.query === INITIAL_FILTERS.query &&
    f.ext === INITIAL_FILTERS.ext &&
    f.size === INITIAL_FILTERS.size &&
    f.modified === INITIAL_FILTERS.modified
  );
}

function iconForMime(mime: string): typeof FileIcon {
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return VideoIcon;
  if (
    mime.includes("pdf") ||
    mime.includes("officedocument") ||
    mime.includes("word") ||
    mime.includes("excel") ||
    mime.includes("powerpoint")
  ) {
    return FileText;
  }
  return FileIcon;
}
